import type { PageReadings } from "@crux/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveAlias,
  fetchAiAccess,
  fetchOpportunities,
  fetchOpportunityInsight,
  fetchOpportunityLens,
  type OpportunitiesQuery,
  type OpportunityLensQuery,
  type ReadAsk,
  readPages,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/** Opportunities over a range: counted on the server, cached there until the data moves. */
export function useOpportunities(
  workspaceId: string,
  reportId: string,
  query: OpportunitiesQuery,
) {
  return useQuery({
    queryKey: queryKeys.opportunities(workspaceId, reportId, query),
    queryFn: () => fetchOpportunities(workspaceId, reportId, query),
  });
}

/** One opportunity's answers. Its drawer mounts only while an item is open. */
export function useOpportunityLens(
  workspaceId: string,
  reportId: string,
  query: OpportunityLensQuery,
) {
  return useQuery({
    queryKey: queryKeys.opportunityLens(workspaceId, reportId, query),
    queryFn: () => fetchOpportunityLens(workspaceId, reportId, query),
  });
}

/** One opportunity looked into: position, tone, searches, the gap before. */
export function useOpportunityInsight(
  workspaceId: string,
  reportId: string,
  query: OpportunityLensQuery,
) {
  return useQuery({
    queryKey: queryKeys.opportunityInsight(workspaceId, reportId, query),
    queryFn: () => fetchOpportunityInsight(workspaceId, reportId, query),
  });
}

/** How often a drawer asks after pages that are moving. */
const READ_EVERY_MS = 4_000;

/**
 * Whether any page is moving: queued or being read, and not stalled. A
 * stalled page has nobody working on it, so asking again changes nothing.
 */
export function isMoving(readings: PageReadings): boolean {
  return readings.pages.some(
    (p) => (p.state === "queued" || p.state === "fetching") && !p.stalled,
  );
}

/**
 * The pages behind an opportunity, read by the server. Asking queues any not
 * read in the last week; while some are moving it asks again every four
 * seconds, and stops once every page is read, failed or stalled — the server
 * says which, so a page nobody picks up is never shown as being read. Off
 * while there is nothing to read.
 */
export function usePageReadings(
  workspaceId: string,
  reportId: string,
  ask: ReadAsk | null,
) {
  const sent: ReadAsk = ask ?? { from: "", to: "", urls: [] };
  return useQuery({
    queryKey: queryKeys.pageReadings(workspaceId, reportId, sent),
    queryFn: () => readPages(workspaceId, reportId, sent),
    enabled: ask !== null,
    refetchInterval: (q) => {
      const data = q.state.data;
      return data !== undefined && isMoving(data) ? READ_EVERY_MS : false;
    },
  });
}

/**
 * The brand's robots.txt, read by the server now. A slow site is the site's
 * business, so this is its own query: the rest of the page never waits on it.
 */
export function useAiAccess(workspaceId: string, reportId: string) {
  return useQuery({
    queryKey: queryKeys.aiAccess(workspaceId, reportId),
    queryFn: () => fetchAiAccess(workspaceId, reportId),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Count a name as a tracked brand: Rivals' "Same company, different name",
 * from an opportunity. Every stored mention of the name moves to the brand,
 * so every screen's figures move with it, and every cached one is let go.
 */
export function useCountName(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly brandId: string; readonly alias: string }) =>
      approveAlias(workspaceId, input),
    onSuccess: () => client.invalidateQueries(),
  });
}
